import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { compressToWebP } from '@/lib/imageUtils';
import { useForm, Controller } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import {
  CheckCircle2, Clock, FileText, IndianRupee, Loader2, ShoppingCart, CreditCard,
} from 'lucide-react';
import type { ApiError } from '@pms/shared';
import { PERMISSIONS } from '@pms/shared';
import { financeService, type PurchaseListItem } from '@/services/finance.service';
import { PermissionGate } from '@/components/guards/PermissionGate';
import { Tabs } from '@/components/ui/tabs';
import { DataTable, type Column } from '@/components/ui/data-table';
import { StatCard } from '@/components/ui/stat-card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { formatINR } from '@/lib/currency';
import { cn } from '@/lib/utils';
import { PaymentModal } from './components/PaymentModal';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

const GST_RATE = 0.18;
const inputCls = 'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const TABS = [
  { key: 'new', label: 'New Purchase' },
  { key: 'list', label: 'Purchase List' },
  { key: 'summary', label: 'Summary' },
];

function statusVariant(s: PurchaseListItem['status']): 'success' | 'warning' | 'default' {
  if (s === 'Paid') return 'success';
  if (s === 'Partially Paid') return 'warning';
  return 'default';
}

// ─── New Purchase Tab ──────────────────────────────────────────────────────────

interface NewPurchaseForm {
  vendor_id: string;
  category_id: string;
  invoice_number: string;
  invoice_date: string;
  purchase_date: string;
  base_amount: string;
  description: string;
}

function NewPurchaseTab({ onCreated }: { onCreated: () => void }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);

  const { data: vendorsData } = useQuery({
    queryKey: ['finance', 'vendors', { all: true }],
    queryFn: () => financeService.listVendors({ limit: 100 }),
  });
  const { data: categories = [] } = useQuery({
    queryKey: ['finance', 'categories'],
    queryFn: () => financeService.listCategories(),
  });

  const { register, handleSubmit, reset, setValue, watch, control, formState: { errors } } = useForm<NewPurchaseForm>({
    defaultValues: {
      vendor_id: '', category_id: '', invoice_number: '', invoice_date: '',
      purchase_date: '', base_amount: '', description: '',
    },
  });

  const categoryId = watch('category_id');
  const baseAmount = Number(watch('base_amount')) || 0;
  const selectedCat = categories.find((c) => c.id === categoryId);
  const gst = selectedCat?.gst_applicable ? Math.round(baseAmount * GST_RATE * 100) / 100 : 0;
  const total = Math.round((baseAmount + gst) * 100) / 100;
  const invoiceDate = watch('invoice_date');
  const purchaseDate = watch('purchase_date');

  const mutation = useMutation({
    mutationFn: async (values: NewPurchaseForm) => {
      const fd = new FormData();
      fd.append('vendor_id', values.vendor_id);
      if (values.category_id) fd.append('category_id', values.category_id);
      fd.append('invoice_number', values.invoice_number.trim());
      if (values.invoice_date) fd.append('invoice_date', values.invoice_date);
      if (values.purchase_date) fd.append('purchase_date', values.purchase_date);
      fd.append('base_amount', String(baseAmount));
      fd.append('gst_amount', String(gst));
      if (values.description.trim()) fd.append('description', values.description.trim());
      if (file) {
        const processed = await compressToWebP(file);
        fd.append('invoice', processed);
      }
      return financeService.createPurchase(fd);
    },
    onSuccess: (res) => {
      if (res.warning) toast.warning(res.warning);
      toast.success('Purchase created');
      reset();
      setFile(null);
      void queryClient.invalidateQueries({ queryKey: ['finance', 'purchases'] });
      void queryClient.invalidateQueries({ queryKey: ['finance', 'vendors'] });
      void queryClient.invalidateQueries({ queryKey: ['finance', 'ledger'] });
      onCreated();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const vendors = vendorsData?.data ?? [];

  return (
    <div className="rounded-xl border bg-card p-3 md:p-6">
      <h2 className="text-lg font-semibold">New Purchase Entry</h2>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="mt-5 space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="np-vendor">Vendor *</Label>
            <Controller
              name="vendor_id" control={control} rules={{ required: 'Select a vendor' }}
              render={({ field }) => (
                <Select value={field.value || undefined} onValueChange={field.onChange}>
                  <SelectTrigger id="np-vendor"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent>
                    {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.vendor_id && <p className="text-xs text-destructive">{errors.vendor_id.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="np-cat">Purchase Category</Label>
            <Controller
              name="category_id" control={control}
              render={({ field }) => (
                <Select value={field.value || undefined} onValueChange={field.onChange}>
                  <SelectTrigger id="np-cat"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="np-inv">Invoice Number *</Label>
            <Input id="np-inv" {...register('invoice_number', { required: 'Invoice number is required' })} />
            {errors.invoice_number && <p className="text-xs text-destructive">{errors.invoice_number.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="np-invdate">Invoice Date</Label>
            <DatePicker id="np-invdate" value={invoiceDate} onChange={(val) => setValue('invoice_date', val)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="np-pdate">Purchase Date</Label>
            <DatePicker id="np-pdate" value={purchaseDate} onChange={(val) => setValue('purchase_date', val)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="np-base">Base Amount (₹) *</Label>
            <Input
              id="np-base" type="number" min="0.01" step="0.01"
              {...register('base_amount', { required: 'Base amount is required', validate: (v) => Number(v) > 0 || 'Enter a positive amount' })}
            />
            {errors.base_amount && <p className="text-xs text-destructive">{errors.base_amount.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label>GST Amount (₹)</Label>
            <div className={cn(inputCls, 'items-center bg-muted/40')}>{formatINR(gst)}</div>
            {selectedCat && !selectedCat.gst_applicable && (
              <p className="text-xs text-muted-foreground">GST not applicable for this category</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Total Amount (₹)</Label>
            <div className={cn(inputCls, 'items-center bg-muted/40 font-semibold')}>{formatINR(total)}</div>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="np-desc">Description</Label>
          <Textarea id="np-desc" {...register('description')} rows={2} />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="np-file">Upload Invoice</Label>
          <Input
            id="np-file" type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <p className="text-xs text-muted-foreground">
            Required when the invoice-upload rule is enabled. PDF, image, or Word, up to 15&nbsp;MB.
          </p>
        </div>

        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShoppingCart className="h-4 w-4 mr-2" />}
          Create Purchase
        </Button>
      </form>
    </div>
  );
}

// ─── Purchase List Tab ─────────────────────────────────────────────────────────

function PurchaseListTab() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [payTarget, setPayTarget] = useState<PurchaseListItem | null>(null);
  const limit = 15;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['finance', 'purchases', { page, limit }],
    queryFn: () => financeService.listPurchases({ page, limit }),
  });
  const { data: modes = [] } = useQuery({
    queryKey: ['finance', 'payment-modes'],
    queryFn: () => financeService.listPaymentModes(),
  });

  const openInvoice = async (id: string) => {
    try {
      const url = await financeService.getInvoiceUrl(id);
      window.open(url, '_blank', 'noopener');
    } catch {
      toast.error('No invoice available');
    }
  };

  const columns: Column<PurchaseListItem>[] = [
    { key: 'vendor', header: 'Vendor', cell: (r) => <span className="font-medium">{r.vendor_name}</span> },
    { key: 'invoice', header: 'Invoice No', cell: (r) => <span>{r.invoice_number}</span> },
    { key: 'category', header: 'Category', cell: (r) => r.category_name ?? <span className="text-muted-foreground">—</span> },
    { key: 'total', header: 'Total', className: 'text-right', cell: (r) => <span className="tabular-nums">{formatINR(r.total_amount)}</span> },
    { key: 'paid', header: 'Paid', className: 'text-right', cell: (r) => <span className="tabular-nums text-emerald-600">{formatINR(r.paid_amount)}</span> },
    { key: 'balance', header: 'Balance', className: 'text-right', cell: (r) => <span className="tabular-nums text-rose-600">{formatINR(r.balance_amount)}</span> },
    { key: 'status', header: 'Status', cell: (r) => <Badge variant={statusVariant(r.status)}>{r.status}</Badge> },
    {
      key: 'actions', header: 'Action', className: 'w-32',
      cell: (r) => (
        <div className="flex items-center gap-1">
          {r.invoice_url && (
            <Button size="sm" variant="ghost" className="h-9 w-9 p-0" onClick={() => void openInvoice(r.id)} title="View invoice">
              <FileText className="h-5 w-5" />
            </Button>
          )}
          {r.status !== 'Paid' && (
            <PermissionGate permission={PERMISSIONS.FINANCE_PURCHASES_PAY}>
              <Button size="sm" variant="outline" className="h-9 gap-1.5" onClick={() => setPayTarget(r)}>
                <CreditCard className="h-4 w-4" /> Pay
              </Button>
            </PermissionGate>
          )}
        </div>
      ),
    },
  ];

  const pagination = data
    ? { page: data.pagination.page, totalPages: data.pagination.totalPages, total: data.pagination.total, onPageChange: setPage }
    : undefined;

  return (
    <>
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(r) => r.id}
        loading={isLoading}
        error={isError}
        onRetry={() => void queryClient.invalidateQueries({ queryKey: ['finance', 'purchases'] })}
        empty="No purchases yet."
        pagination={pagination}
      />
      {payTarget && (
        <PaymentModal
          purchase={payTarget}
          modes={modes}
          onClose={() => setPayTarget(null)}
          onSuccess={() => setPayTarget(null)}
        />
      )}
    </>
  );
}

// ─── Summary Tab ───────────────────────────────────────────────────────────────

function SummaryTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['finance', 'purchases', 'summary'],
    queryFn: () => financeService.getSummary(),
  });

  const cards = useMemo(() => ([
    { label: 'Total Purchase (This Month)', value: data ? formatINR(data.total_purchase_month) : '—', tone: 'sky' as const, icon: ShoppingCart },
    { label: 'Total Paid (This Month)', value: data ? formatINR(data.total_paid_month) : '—', tone: 'emerald' as const, icon: CheckCircle2 },
    { label: 'Total Outstanding', value: data ? formatINR(data.total_outstanding) : '—', tone: 'rose' as const, icon: Clock },
    { label: 'Vendors with Pending', value: data ? data.vendors_with_pending : '—', tone: 'amber' as const, icon: IndianRupee },
  ]), [data]);

  if (isLoading) {
    return <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-xl bg-muted/40" />)}
    </div>;
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((c) => <StatCard key={c.label} label={c.label} value={c.value} tone={c.tone} icon={c.icon} />)}
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export function PurchasesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') ?? 'new';
  const setTab = (key: string) => setSearchParams({ tab: key });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Purchase Management</h1>
        <p className="text-sm text-muted-foreground">Create purchases and process payments</p>
      </div>

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      <div className="mt-2">
        {tab === 'new' && <NewPurchaseTab onCreated={() => setTab('list')} />}
        {tab === 'list' && <PurchaseListTab />}
        {tab === 'summary' && <SummaryTab />}
      </div>
    </div>
  );
}
